import type { RankedThrow } from "./rankedSimulation";
import { RULES_VERSION, type GameModeId } from "./rules";
import { readStoredValue, writeStoredValue } from "./storage";

export interface LeaderboardEntry {
  name: string;
  score: number;
  achievedAt: number;
}

export interface Leaderboards {
  global: LeaderboardEntry[];
  daily: LeaderboardEntry[];
  crew: LeaderboardEntry[];
  day: string;
}

export interface RankedRun {
  id: string;
  mode: Extract<GameModeId, "moving" | "daily">;
  seed: number;
  day: string;
  crewCode: string;
  validAfter: number;
  expiresAt: number;
  rulesVersion: number;
}

export interface PendingRankedSubmission {
  run: RankedRun;
  playerName: string;
  trace: RankedThrow[];
}

export interface SubmissionResult extends Leaderboards {
  placement: number | null;
}

export async function issueRankedRun(
  mode: Extract<GameModeId, "moving" | "daily">,
  clientId: string,
  crewCode: string,
): Promise<RankedRun> {
  return requestJson<RankedRun>("/api/runs", {
    method: "POST",
    body: JSON.stringify({ mode, clientId, crewCode, rulesVersion: RULES_VERSION }),
  });
}

export async function submitRankedRun(
  submission: PendingRankedSubmission,
  clientId: string,
): Promise<SubmissionResult> {
  return requestJson<SubmissionResult>("/api/submissions", {
    method: "POST",
    body: JSON.stringify({
      runId: submission.run.id,
      clientId,
      playerName: submission.playerName,
      trace: submission.trace,
    }),
  });
}

export async function fetchLeaderboards(mode: GameModeId, crewCode: string): Promise<Leaderboards> {
  const query = new URLSearchParams({ mode, crew: crewCode, rules: String(RULES_VERSION) });
  return requestJson<Leaderboards>(`/api/leaderboards?${query}`);
}

export function readClientId(): string {
  const key = "hoop-dreams-client-id";
  const existing = readStoredValue(key);
  if (existing && /^[a-f0-9]{32}$/.test(existing)) return existing;
  const created = crypto.randomUUID().replaceAll("-", "");
  writeStoredValue(key, created);
  return created;
}

export function createCrewCode(): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const bytes = new Uint8Array(10);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (value) => alphabet[value % alphabet.length]).join("");
}

async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 8_000);

  try {
    const response = await fetch(path, {
      ...init,
      signal: controller.signal,
      headers: { "content-type": "application/json", ...init?.headers },
    });
    const payload = await response.json() as T & { error?: string };
    if (!response.ok) throw new Error(payload.error ?? `Request failed (${response.status})`);
    return payload;
  } finally {
    window.clearTimeout(timeout);
  }
}
